import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function FAQ() {
  const { t, i18n } = useTranslation();
  const [dbFaqs, setDbFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = i18n.language;

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        if (Array.isArray(response.data)) {
          setDbFaqs(response.data);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  const translatedItems = t("faq.items", { returnObjects: true });
  const hasTranslatedItems = lang !== 'lv' && Array.isArray(translatedItems) && translatedItems.length > 0;

  const faqs = hasTranslatedItems
    ? translatedItems.map((item, i) => ({ id: `faq-${i}`, question: item.question, answer: item.answer }))
    : dbFaqs;

  if (loading && !hasTranslatedItems) {
    return (
      <section className="section-spacing gradient-bg">
        <div className="section-container text-center">
          <p className="text-muted-foreground">{t("faq.loading")}</p>
        </div>
      </section>
    );
  }

  if (faqs.length === 0) return null;

  return (
    <section id="buj" className="section-spacing gradient-bg" data-testid="faq-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">{t("faq.badge")}</span>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("faq.title")}</h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">{t("faq.description")}</p>
            <div className="relative rounded-2xl overflow-hidden img-zoom hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1758691462749-a95ce1bd7f96"
                alt="Senioru aprūpes konsultācija — attālinātā veselības uzraudzība un profesionāls atbalsts"
                className="w-full h-64 object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={faq.id} value={faq.id} className="bg-white rounded-2xl border border-border px-6 data-[state=open]:shadow-md transition-shadow" data-testid={`faq-item-${index + 1}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold text-foreground hover:no-underline py-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
